import React from 'react';

import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { GeolocationDeniedModal } from 'components/geolocation-denied-modal';
import { GoogleGeolocationLibraryLoader } from 'components/google-geolocation-library-loader';
import { StoreLocatorEmptyStates as EmptyState } from 'components/store-locator-empty-states';
import useMediaQuery from 'hooks/use-media-query';
import { useGeolocation } from 'state/geolocation';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { OrderLocatorTab } from 'state/launchdarkly/variations';
import { routes } from 'utils/routing';

import { StoreLocatorProvider, useStoreLocatorContext } from '../hooks';
import StoreLocatorMeta from '../meta';
import { DeliveryRoutes } from '../routes';
import { SearchBar } from '../search-bar';
import { ViewContainer } from '../styled/view-container';
import { TabPanels, TabsBar } from '../tabs';

import { BackArrow } from './back-arrow';
import FittedViewContainer from './fitted-view-container';
import useStoreLocatorRedirects from './hooks/use-store-locator-redirects';
import { ListView } from './list-view';
import { MapView } from './map-view';
import { StoreCardsList } from './store-cards';
import StoreLocatorRoutes from './store-locator-routes';
import { ToggleViewButton } from './toggle-view-button';

export const SearchBarWrapper = styled.div<{ isListView?: boolean; smallSize?: boolean }>`
  margin: 1rem auto;
  width: ${p => (p.smallSize ? 'calc(100% - 8rem)' : 'calc(100% - 2rem)')};
  display: flex;
  position: ${p => (p.isListView ? 'relative' : 'absolute')};
  left: ${p => (p.isListView ? '0' : '1rem')};
  z-index: ${Styles.zIndex.top};
  ${Styles.desktop`
    left: 0;
    margin: 0 auto;
    position: relative;
    z-index: auto;
    width: 100%;
  `}
`;

export function StoreLocator() {
  const {
    ErrorDialog,
    Dialog,
    activeStoreId,
    redirectUrl,
    searchingNearby,
    searchingRecent,
    searchingFavs,
    currentTabHasError,
    nearbyStoreCardRefs,
    storesNearby,
    storesFavs,
    storesRecent,
    currentTab,
    setIsStoreInfoModalOpen,
    handleTabChange,
    setActiveStoreId,
    locatorTabs,
    currentTabIndex,
    locatorTabIds,
    currentView,
    currentViewIsList,
    currentViewIsMap,
    updateStoreLocatorView,
  } = useStoreLocatorContext();

  useStoreLocatorRedirects();

  const {
    updateManualCoordinates,
    manualAddress,
    setManualAddress,
    coordinatesAvailable,
    isPermissionChecking,
    isPermissionGranted,
    resetCoordsAndLoadCurrentPosition,
  } = useGeolocation();

  const navigate = useNavigate();

  const [dismissAddressModalUrl, setDismissAddressModalUrl] = React.useState(routes.base);
  const [geolocationDeniedModalOpen, setGeolocationDeniedModalOpen] = React.useState(false);

  const enableDeliveryOnly = useFlag(LaunchDarklyFlag.ENABLE_DELIVERY_ONLY);

  const isDesktop = useMediaQuery('desktop');

  const handleTabChangeEvent = React.useCallback(
    (event: React.ChangeEvent<{}>, newValue: number) => {
      event.preventDefault();
      if (newValue === locatorTabIds[OrderLocatorTab.DELIVERY]) {
        navigate(routes.address);
        setDismissAddressModalUrl(routes.storeLocator);
        return;
      }

      handleTabChange(newValue);
    },
    [handleTabChange, locatorTabIds, navigate]
  );

  const openGeolocationDeniedModal = () => setGeolocationDeniedModalOpen(true);
  const dismissGeolocationDeniedModal = () => setGeolocationDeniedModalOpen(false);

  /**
   *   - searchingNearby
   *     - AND we have a coordinates to search
   *       (we need manual|automatic coord to search something - without that we don't search)
   *       (this also handles the case where we know permissions are denied, but we have manual coords)
   *   - we're checking permissions
   *   - we have granted permissions
   *     - AND we have no available coords to search
   *     (ex. we're in the waiting time between getting granted permissions
   *      and actually receiving the user's coords)
   */
  const isSearchingNearby =
    (searchingNearby && coordinatesAvailable) ||
    isPermissionChecking ||
    (isPermissionGranted && !coordinatesAvailable);

  const isSearchingRecent = searchingRecent || isPermissionChecking;
  const isSearchingFavorites = searchingFavs || isPermissionChecking;

  const searchBarEl = (
    <SearchBarWrapper
      smallSize={currentViewIsMap}
      isListView={currentViewIsList}
      data-testid="locator-search-bar"
    >
      <SearchBar
        autoFocus={!manualAddress && isPermissionGranted && isDesktop}
        onAddressSelected={updateManualCoordinates}
        onInputValueChange={setManualAddress}
        value={manualAddress}
      />
    </SearchBarWrapper>
  );

  const noStoresElement = (
    <EmptyState
      tab={currentTab}
      locationPermissionsGranted={isPermissionGranted}
      coordinatesAvailable={coordinatesAvailable}
      currentTabError={currentTabHasError}
    />
  );

  const nearbyStoreCardsEl = (
    <StoreCardsList
      searching={isSearchingNearby}
      stores={storesNearby}
      noStoresElement={noStoresElement}
      nearbyStoreCardRefs={nearbyStoreCardRefs}
      setActiveStoreId={setActiveStoreId}
      activeStoreId={activeStoreId}
    />
  );

  const favStoreCardsEl = (
    <StoreCardsList
      searching={isSearchingFavorites}
      stores={storesFavs}
      noStoresElement={noStoresElement}
      nearbyStoreCardRefs={nearbyStoreCardRefs}
      setActiveStoreId={setActiveStoreId}
      activeStoreId={activeStoreId}
    />
  );

  const recentStoreCardsEl = (
    <StoreCardsList
      searching={isSearchingRecent}
      stores={storesRecent}
      noStoresElement={noStoresElement}
      nearbyStoreCardRefs={nearbyStoreCardRefs}
      setActiveStoreId={setActiveStoreId}
      activeStoreId={activeStoreId}
    />
  );

  const cardsByTab = {
    [OrderLocatorTab.NEARBY]: nearbyStoreCardsEl,
    [OrderLocatorTab.FAVORITE]: favStoreCardsEl,
    [OrderLocatorTab.RECENT]: recentStoreCardsEl,
  };

  const tabsEl = (
    <TabsBar
      locatorTabs={locatorTabs}
      locatorTabIds={locatorTabIds}
      currentTabIndex={currentTabIndex}
      handleTabChangeEvent={handleTabChangeEvent}
    />
  );

  const tabPanelsEl = (
    <TabPanels
      locatorTabs={locatorTabs}
      locatorTabIds={locatorTabIds}
      currentTabIndex={currentTabIndex}
      cardsByTab={cardsByTab}
    />
  );

  // If the only service mode available is delivery, don't allow users to go to the store locator
  if (enableDeliveryOnly) {
    return (
      <DeliveryRoutes
        deliveryModalPath="*"
        dismissAddressModalUrl={dismissAddressModalUrl}
        redirectUrl={redirectUrl}
      />
    );
  }

  return (
    <GoogleGeolocationLibraryLoader>
      <StoreLocatorMeta />
      <GeolocationDeniedModal
        isOpen={geolocationDeniedModalOpen}
        dismissModal={dismissGeolocationDeniedModal}
      />
      <FittedViewContainer>
        <BackArrow />
        <ViewContainer data-testid="store-locator-container">
          <ToggleViewButton
            currentView={currentView}
            updateStoreLocatorView={updateStoreLocatorView}
          />
          <MapView
            handleResetLocation={
              isPermissionGranted ? resetCoordsAndLoadCurrentPosition : openGeolocationDeniedModal
            }
            searchBarEl={searchBarEl}
            tabsEl={tabsEl}
            tabPanelsEl={tabPanelsEl}
            nearbyStoreCardsEl={nearbyStoreCardsEl}
          />
          <ListView
            currentViewIsList={currentViewIsList}
            tabsEl={tabsEl}
            tabPanelsEl={tabPanelsEl}
            searchBarEl={searchBarEl}
          />
          <StoreLocatorRoutes
            dismissAddressModalUrl={dismissAddressModalUrl}
            redirectUrl={redirectUrl}
            setIsStoreInfoModalOpen={setIsStoreInfoModalOpen}
          />
        </ViewContainer>
      </FittedViewContainer>
      <ErrorDialog />
      <Dialog />
    </GoogleGeolocationLibraryLoader>
  );
}

function StoreLocatorWithProvider() {
  return (
    <StoreLocatorProvider>
      <StoreLocator />
    </StoreLocatorProvider>
  );
}

export default StoreLocatorWithProvider;
